import type { ReactNode } from "react";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Upload,
  FileSpreadsheet,
  Receipt,
  ListChecks,
  Bell,
  Banknote,
  Inbox,
  Sparkles,
  Files,
  Building2,
  Archive,
  Send,
  ListOrdered,
  Paperclip,
  Users,
  LogOut,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { FilialSelector } from "@/components/topbar/FilialSelector";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  exact?: boolean;
  group?: string;
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true, group: "Visão geral" },
  { to: "/importacoes/extratos", label: "Importar arquivos", icon: Upload, group: "Importações" },
  { to: "/importacoes", label: "Histórico de importações", icon: Inbox, group: "Importações" },
  // { to: "/extratos-bancarios", label: "Extratos Bancários", icon: FileSpreadsheet, group: "Operação" },
  { to: "/gerenciador-anexacao", label: "Gerenciador de anexação", icon: Paperclip, group: "Operação" },
  { to: "/lancamentos", label: "Histórico de lançamentos", icon: ListChecks, group: "Operação" },
  { to: "/comprovantes", label: "Histórico de comprovantes", icon: Receipt, group: "Operação" },
  { to: "/sugestoes-combinacoes", label: "Sugestões", icon: Sparkles, group: "Operação" },
  { to: "/arquivos", label: "Arquivos", icon: Files, group: "Operação" },
  { to: "/transmissoes", label: "Transmissões", icon: Send, group: "Transmissão" },
  { to: "/transmissoes/fila", label: "Fila de transmissão", icon: ListOrdered, group: "Transmissão" },
  { to: "/caixas", label: "Caixas", icon: Archive, group: "Cadastros" },
  { to: "/filiais", label: "Filiais", icon: Building2, group: "Cadastros" },
  { to: "/usuarios", label: "Usuários", icon: Users, group: "Administração", adminOnly: true },
];

const PAGE_TITLES: { match: (path: string) => boolean; title: string; subtitle?: string }[] = [
  { match: (p) => p === "/", title: "Dashboard", subtitle: "Visão geral das operações" },
  {
    match: (p) => p.startsWith("/importacoes/extratos"),
    title: "Importar arquivos",
    subtitle: "Envie um arquivo e dispare o processamento",
  },
  {
    match: (p) => /^\/importacoes\/[^/]+/.test(p),
    title: "Detalhe da Importação",
  },
  {
    match: (p) => p.startsWith("/importacoes"),
    title: "Histórico de importações",
    subtitle: "Histórico de importações de extratos",
  },
  {
    match: (p) => /^\/lancamentos\/[^/]+/.test(p),
    title: "Detalhe do Lançamento",
  },
  {
    match: (p) => p.startsWith("/sugestoes-combinacoes"),
    title: "Sugestões de Combinações",
    subtitle: "Combinações sugeridas entre comprovantes e lançamentos",
  },
  {
    match: (p) => p.startsWith("/arquivos"),
    title: "Arquivos",
    subtitle: "Repositório de arquivos do sistema",
  },
  {
    match: (p) => /^\/extratos-bancarios\/[^/]+/.test(p),
    title: "Detalhe do Extrato Bancário",
  },
  {
    match: (p) => p.startsWith("/extratos-bancarios"),
    title: "Extratos Bancários",
    subtitle: "Lista de extratos importados",
  },
  {
    match: (p) => p.startsWith("/lancamentos"),
    title: "Histórico de lançamentos",
    subtitle: "Movimentações dos extratos",
  },
  {
    match: (p) => /^\/comprovantes\/[^/]+/.test(p),
    title: "Detalhe do Comprovante",
  },
  {
    match: (p) => p.startsWith("/comprovantes"),
    title: "Histórico de comprovantes",
    subtitle: "Comprovantes processados e suas combinações",
  },
  {
    match: (p) => p.startsWith("/gerenciador-anexacao"),
    title: "Gerenciador de anexação",
    subtitle: "Vincule comprovantes aos lançamentos",
  },
  {
    match: (p) => /^\/caixas\/[^/]+/.test(p),
    title: "Detalhe da Caixa",
  },
  {
    match: (p) => p.startsWith("/caixas"),
    title: "Caixas",
    subtitle: "Cadastro e listagem de caixas",
  },
  {
    match: (p) => p.startsWith("/filiais"),
    title: "Filiais",
    subtitle: "Cadastro de filiais da operação",
  },
  {
    match: (p) => p === "/transmissoes/fila",
    title: "Fila de transmissão",
    subtitle: "Lançamentos aguardando envio ao Fluig",
  },
  {
    match: (p) => /^\/transmissoes\/[^/]+/.test(p),
    title: "Detalhe da Transmissão",
  },
  {
    match: (p) => p.startsWith("/transmissoes"),
    title: "Transmissões",
    subtitle: "Histórico de transmissões para o Fluig",
  },
  {
    match: (p) => p.startsWith("/usuarios"),
    title: "Usuários",
    subtitle: "Gerenciamento de usuários e permissões",
  },
];

function getPageInfo(path: string) {
  return (
    PAGE_TITLES.find((p) => p.match(path)) ?? { title: "AXIS Comprovantes", subtitle: undefined }
  );
}

export function AppLayout({ children }: { children?: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const page = getPageInfo(pathname);
  const { perfil, sair, saindo } = useAuth();
  const navigate = useNavigate();
  const podeGerenciarUsuarios = !!(perfil?.admin || perfil?.root);

  async function handleSair() {
    try {
      await sair();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Não foi possível encerrar a sessão. Tente novamente.",
      );
      return;
    }
    void navigate({ to: "/login", replace: true });
  }

  const iniciais = (perfil?.nome ?? "AX")
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");

  // agrupar nav
  const visibleNav = NAV.filter((item) => !item.adminOnly || podeGerenciarUsuarios);

  const groups = visibleNav.reduce<Record<string, NavItem[]>>((acc, item) => {
    const g = item.group ?? "Outros";
    (acc[g] ||= []).push(item);
    return acc;
  }, {});

  const isActive = (item: NavItem) =>
    item.exact ? pathname === item.to : pathname === item.to || pathname == (item.to + "/");

  return (
    <div className="min-h-screen flex w-full bg-background text-foreground">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border">
        <div className="px-5 py-5 flex items-center gap-2.5 border-b border-sidebar-border">
          <div className="h-9 w-9 rounded-lg bg-sidebar-primary text-sidebar-primary-foreground flex items-center justify-center">
            <Banknote className="h-5 w-5" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-semibold tracking-tight">AXIS</span>
            <span className="text-xs text-sidebar-foreground/60">Comprovantes</span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group}>
              <div className="px-2 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                {group}
              </div>
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item);
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        className={cn(
                          "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
                          active
                            ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                            : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="px-4 py-3 border-t border-sidebar-border text-xs text-sidebar-foreground/60">
          v1.0 · pt-BR
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur border-b">
          <div className="flex items-center justify-between gap-4 px-4 sm:px-6 py-3">
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold truncate">{page.title}</h1>
              {page.subtitle && (
                <p className="text-xs text-muted-foreground truncate">{page.subtitle}</p>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <FilialSelector />
              <button
                type="button"
                className="relative h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground"
                aria-label="Notificações"
              >
                <Bell className="h-4.5 w-4.5" />
                <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-destructive" />
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void handleSair()}
                disabled={saindo}
                aria-label="Sair"
                className="text-muted-foreground"
              >
                {saindo ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <LogOut className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Sair</span>
              </Button>
              <div className="hidden sm:flex items-center gap-2 pl-3 border-l">
                <Avatar className="h-8 w-8">
                  {perfil?.foto_url && <AvatarImage src={perfil.foto_url} alt={perfil.nome} />}
                  <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                    {iniciais || "AX"}
                  </AvatarFallback>
                </Avatar>
                <div className="leading-tight max-w-45">
                  <div className="text-sm font-medium truncate">{perfil?.nome ?? "—"}</div>
                  <div className="text-xs text-muted-foreground truncate">{perfil?.email ?? ""}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile nav */}
          <div className="md:hidden border-t overflow-x-auto">
            <ul className="flex gap-1 px-2 py-2">
              {visibleNav.map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={cn(
                        "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs whitespace-nowrap",
                        active
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 py-5 sm:py-6">
          <div className="mx-auto max-w-8xl">{children ?? <Outlet />}</div>
        </main>
      </div>
    </div>
  );
}
